import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Check, Download, ExternalLink, FileText, RotateCcw, Search, X, ZoomIn,
} from 'lucide-react';
import { ApiClientError, EmptyState, Spinner, StatusBadge, apiUrl, relativeTime } from '@riskpay/ui-components';
import { api, useAuth } from '../lib/client';

/* ==========================================================================
 *  KYC control
 *
 *  The operations console shows an analyst only what is queued for review.
 *  A Super Administrator needs the other thing: any merchant, in any state,
 *  at any time — including approved ones, months later, when an acquirer or
 *  a regulator asks to see what was actually verified.
 *
 *  So this screen is browse-first: search every merchant, open the dossier,
 *  read or download any document, and act on it. Downloads go through the
 *  same authorised, audited route the inline viewer uses — there is no public
 *  or pre-signed URL to leak.
 * ========================================================================== */

interface MerchantRow {
  id: string;
  merchant_ref: string;
  legal_name: string;
  display_name: string;
  entity_type: string;
  industry_category: string;
  kyc_status: string;
  risk_tier: string;
  is_frozen: number;
  created_at: string;
}

interface DocumentRow {
  id: string;
  document_type: string;
  holder_name: string | null;
  file_name: string;
  mime_type: string;
  file_size_bytes: number;
  verification_status: string;
  rejection_comment: string | null;
  version: number;
  uploaded_at: string;
}

interface Dossier {
  merchant: MerchantRow & {
    support_email: string;
    support_phone: string | null;
    website_url: string | null;
    pan_number: string | null;
    gstin: string | null;
    cin: string | null;
    udyam_number: string | null;
    kyc_submitted_at: string | null;
    kyc_rejection_reason: string | null;
    risk_score: number;
  };
  documents: DocumentRow[];
  contacts: { id: string; full_name: string; email: string; phone: string | null; role: string }[];
  bank_accounts: {
    id: string;
    rail: 'BANK' | 'CRYPTO';
    label: string | null;
    account_holder_name: string | null;
    account_last4: string | null;
    ifsc_code: string | null;
    bank_name: string | null;
    asset: string | null;
    chain: string | null;
    wallet_address: string | null;
    is_primary: number;
    status: string;
  }[];
  lifetime: { attempts: number; successes: number; gtv: string } | null;
}

const STATUSES = ['', 'DRAFT', 'PENDING_REVIEW', 'ACTION_REQUIRED', 'APPROVED', 'REJECTED', 'SUSPENDED'];

/** Fetches a document with the bearer token attached and hands back a blob URL. */
function useDocumentBlob(documentId: string | null, token: string | null) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!documentId) {
      setUrl(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    setError(null);
    setUrl(null);

    void (async () => {
      try {
        // An <object src> or <img src> sends no Authorization header, so the
        // bytes have to come through fetch and become a blob URL.
        const response = await fetch(apiUrl(`/api/v1/quantum/documents/${documentId}/content`), {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) throw new Error(`Could not load the document (HTTP ${response.status}).`);
        const blob = await response.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [documentId, token]);

  return { url, error };
}

function Identifier({ label, value, mask }: { label: string; value: string | null; mask?: boolean }) {
  const display = !value ? '—' : mask && value.length === 10 ? `${value.slice(0, 3)}•••••${value.slice(-2)}` : value;
  return (
    <div>
      <dt className="meta">{label}</dt>
      <dd className="numeral mt-1 text-sm text-content">{display}</dd>
    </div>
  );
}

function Dossier({ merchantId, onBack }: { merchantId: string; onBack: () => void }) {
  const queryClient = useQueryClient();
  const token = useAuth((s) => s.session?.accessToken ?? null);
  const [selected, setSelected] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['kyc-dossier', merchantId],
    queryFn: () => api.get<Dossier>(`/merchants/${merchantId}`),
  });

  const doc = data?.documents.find((d) => d.id === selected) ?? null;
  const { url: blobUrl, error: blobError } = useDocumentBlob(selected, token);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['kyc-dossier', merchantId] });
    void queryClient.invalidateQueries({ queryKey: ['kyc-merchants'] });
  };
  const fail = (err: unknown) => setError(err instanceof ApiClientError ? err.message : 'That action was rejected.');

  const reviewDoc = useMutation({
    mutationFn: (input: { id: string; status: string; comment?: string }) =>
      api.post(`/documents/${input.id}/review`, { status: input.status, comment: input.comment }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: fail,
  });

  const approve = useMutation({
    mutationFn: () => api.post(`/merchants/${merchantId}/kyc/approve`, { reason: reason || undefined }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: fail,
  });

  const reject = useMutation({
    mutationFn: () => api.post(`/merchants/${merchantId}/kyc/reject`, { reason }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: fail,
  });

  if (isLoading || !data) {
    return (
      <div className="flex items-center gap-2 py-20 text-sm text-content-muted">
        <Spinner /> Loading dossier
      </div>
    );
  }

  const m = data.merchant;
  const unresolved = data.documents.filter((d) => d.verification_status !== 'VERIFIED').length;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={onBack} className="btn btn-ghost btn-sm -ml-2">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          All merchants
        </button>
        <div className="flex items-center gap-2">
          <StatusBadge status={m.kyc_status} />
          <StatusBadge status={m.risk_tier} label={`${m.risk_tier} · ${m.risk_score}`} />
          {m.is_frozen === 1 && <span className="badge badge-danger">frozen</span>}
        </div>
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        {/* ---- Declared data --------------------------------------------- */}
        <div className="space-y-5">
          <section className="panel p-5">
            <p className="meta">{m.merchant_ref}</p>
            <h2 className="mt-1.5 text-xl font-semibold tracking-tight">{m.legal_name}</h2>
            <p className="mt-1 text-sm text-content-muted">
              Trading as {m.display_name} · {m.entity_type.replace(/_/g, ' ').toLowerCase()} ·{' '}
              {m.industry_category.replace(/_/g, ' ').toLowerCase()}
            </p>

            <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
              <Identifier label="PAN" value={m.pan_number} mask />
              <Identifier label="GSTIN" value={m.gstin} />
              <Identifier label="CIN" value={m.cin} />
              <Identifier label="Udyam" value={m.udyam_number} />
              <Identifier label="Support email" value={m.support_email} />
              <Identifier label="Phone" value={m.support_phone} />
            </dl>

            {m.website_url && (
              <a href={m.website_url} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm mt-5">
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                Open website
              </a>
            )}
          </section>

          <section className="panel">
            <h3 className="border-b border-line px-5 py-3 text-sm font-medium">Payout destinations</h3>
            <ul className="divide-y divide-line-subtle">
              {data.bank_accounts.map((b) => (
                <li key={b.id} className="flex items-start justify-between gap-3 px-5 py-3.5">
                  <div className="min-w-0">
                    <p className="truncate text-sm text-content">
                      {b.rail === 'CRYPTO'
                        ? `${b.asset} · ${b.chain}`
                        : (b.account_holder_name ?? b.bank_name ?? 'Bank account')}
                      {b.is_primary ? ' · primary' : ''}
                    </p>
                    {/* The wallet address is shown whole: it is the only field on a
                        crypto destination worth checking, and a truncated one
                        cannot be checked at all. */}
                    <p className="numeral mt-1 break-all text-xs text-content-faint">
                      {b.rail === 'CRYPTO'
                        ? b.wallet_address
                        : `${b.bank_name} · ••••${b.account_last4} · ${b.ifsc_code}`}
                    </p>
                  </div>
                  <StatusBadge status={b.status} />
                </li>
              ))}
              {data.bank_accounts.length === 0 && (
                <li className="px-5 py-6 text-center text-sm text-content-faint">No payout destination on file.</li>
              )}
            </ul>
          </section>

          <section className="panel">
            <h3 className="border-b border-line px-5 py-3 text-sm font-medium">
              Documents · {data.documents.length}
            </h3>
            <ul className="divide-y divide-line-subtle">
              {data.documents.map((d) => {
                const isOpen = d.id === selected;
                return (
                  <li key={d.id} className={isOpen ? 'bg-surface-overlay' : ''}>
                    <div className="flex items-center gap-3 px-5 py-3.5">
                      <button
                        type="button"
                        onClick={() => setSelected(isOpen ? null : d.id)}
                        className="flex min-w-0 flex-1 items-center gap-3 text-left"
                      >
                        <FileText className="h-4 w-4 shrink-0 text-content-subtle" aria-hidden="true" />
                        <span className="min-w-0">
                          <span className="block truncate text-sm text-content">
                            {d.document_type.replace(/_/g, ' ').toLowerCase()}
                            {d.holder_name && ` · ${d.holder_name}`}
                          </span>
                          <span className="mt-1 block truncate text-xs text-content-faint">
                            {d.file_name} · v{d.version} · {(d.file_size_bytes / 1024).toFixed(0)} KB ·{' '}
                            {relativeTime(d.uploaded_at)}
                          </span>
                        </span>
                      </button>
                      <StatusBadge status={d.verification_status} />
                      <button
                        type="button"
                        onClick={() => setSelected(d.id)}
                        className="btn btn-ghost btn-sm shrink-0"
                        title="View"
                      >
                        <ZoomIn className="h-3.5 w-3.5" aria-hidden="true" />
                      </button>
                    </div>

                    {isOpen && (
                      <div className="flex flex-wrap gap-2 border-t border-line-subtle px-5 py-3">
                        <button
                          type="button"
                          onClick={() => reviewDoc.mutate({ id: d.id, status: 'VERIFIED' })}
                          className="btn btn-secondary btn-sm"
                        >
                          <Check className="h-3.5 w-3.5 text-brand-400" aria-hidden="true" />
                          Verify
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            reviewDoc.mutate({
                              id: d.id,
                              status: 'REUPLOAD_REQUESTED',
                              comment: 'Please upload a clearer copy of this document.',
                            })
                          }
                          className="btn btn-secondary btn-sm"
                        >
                          <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                          Request re-upload
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            reviewDoc.mutate({
                              id: d.id,
                              status: 'REJECTED',
                              comment: 'Document does not meet requirements.',
                            })
                          }
                          className="btn btn-secondary btn-sm text-danger-text"
                        >
                          <X className="h-3.5 w-3.5" aria-hidden="true" />
                          Reject
                        </button>
                        {blobUrl && (
                          <a href={blobUrl} download={d.file_name} className="btn btn-secondary btn-sm ml-auto">
                            <Download className="h-3.5 w-3.5" aria-hidden="true" />
                            Download
                          </a>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
              {data.documents.length === 0 && (
                <li className="px-5 py-8 text-center text-sm text-content-faint">
                  This merchant has not uploaded anything yet.
                </li>
              )}
            </ul>
          </section>
        </div>

        {/* ---- Viewer ----------------------------------------------------- */}
        <div className="panel min-h-[540px] overflow-hidden xl:sticky xl:top-4 xl:self-start">
          {!doc ? (
            <div className="flex h-full min-h-[540px] items-center justify-center px-6 text-center">
              <div>
                <FileText className="mx-auto h-6 w-6 text-content-faint" aria-hidden="true" />
                <p className="mt-3 text-sm text-content-subtle">Select a document to read it here.</p>
              </div>
            </div>
          ) : (
            <div className="flex h-full flex-col">
              <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm text-content">{doc.file_name}</p>
                  <p className="mt-1 text-xs text-content-faint">
                    v{doc.version} · uploaded {relativeTime(doc.uploaded_at)}
                  </p>
                </div>
                {blobUrl && (
                  <div className="flex shrink-0 gap-2">
                    <a href={blobUrl} download={doc.file_name} className="btn btn-secondary btn-sm">
                      <Download className="h-3.5 w-3.5" aria-hidden="true" />
                      Download
                    </a>
                    <a href={blobUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
                      <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                    </a>
                  </div>
                )}
              </div>

              <div className="flex flex-1 items-center justify-center bg-surface-inset p-3">
                {blobError ? (
                  <p className="max-w-sm text-center text-sm text-danger-text">{blobError}</p>
                ) : !blobUrl ? (
                  <span className="flex items-center gap-2 text-sm text-content-subtle">
                    <Spinner /> Loading document
                  </span>
                ) : doc.mime_type === 'application/pdf' ? (
                  <object data={blobUrl} type="application/pdf" className="h-full min-h-[460px] w-full rounded">
                    <p className="p-6 text-center text-sm text-content-subtle">
                      This browser cannot display the PDF inline — use Download above.
                    </p>
                  </object>
                ) : (
                  <img src={blobUrl} alt={doc.file_name} className="mx-auto max-h-[560px] rounded object-contain" />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ---- Decision -------------------------------------------------- */}
      <section className="panel p-5">
        {error && (
          <p role="alert" className="mb-4 rounded-md border border-danger-ring bg-danger-soft px-3 py-2.5 text-sm text-danger-text">
            {error}
          </p>
        )}
        <label htmlFor="kyc-reason" className="label">
          Decision note — written to the immutable audit log, and shown to the merchant on a rejection
        </label>
        <textarea
          id="kyc-reason"
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="input h-auto py-2.5 leading-relaxed"
          placeholder="Why this decision was reached."
        />

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-content-subtle">
            {unresolved > 0
              ? `${unresolved} document${unresolved === 1 ? '' : 's'} unverified — approval stays blocked until each is resolved.`
              : 'All documents verified. Ready for a decision.'}
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => reject.mutate()}
              className="btn btn-danger"
              disabled={reason.trim().length < 10 || reject.isPending}
              title={reason.trim().length < 10 ? 'A rejection needs a reason of at least 10 characters.' : undefined}
            >
              <X className="h-4 w-4" aria-hidden="true" />
              Reject
            </button>
            <button
              type="button"
              onClick={() => approve.mutate()}
              className="btn btn-primary"
              disabled={unresolved > 0 || approve.isPending}
            >
              {approve.isPending ? <Spinner /> : <Check className="h-4 w-4" aria-hidden="true" />}
              Approve merchant
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

export function KycControl() {
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['kyc-merchants', status, search],
    queryFn: () =>
      api.get<{ data: MerchantRow[] }>('/merchants', {
        status: status || undefined,
        search: search.trim() || undefined,
      }),
  });

  if (selected) return <Dossier merchantId={selected} onBack={() => setSelected(null)} />;

  return (
    <div className="space-y-4">
      <p className="max-w-prose text-sm text-content-muted">
        Every merchant, in any state. Open a dossier to read or download any document at any time — approved accounts
        included, which is what an acquirer audit asks for.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[260px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-content-faint" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Merchant reference or legal name"
            className="input pl-9"
            aria-label="Search merchants"
          />
        </div>
        {STATUSES.map((value) => (
          <button
            key={value || 'ALL'}
            type="button"
            onClick={() => setStatus(value)}
            aria-pressed={status === value}
            className={`badge ${status === value ? 'badge-success' : 'badge-neutral'} cursor-pointer`}
          >
            {value ? value.replace(/_/g, ' ').toLowerCase() : 'all'}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 py-16 text-sm text-content-muted">
          <Spinner /> Loading merchants
        </div>
      ) : (data?.data ?? []).length === 0 ? (
        <div className="panel">
          <EmptyState title="No merchants match" description="Clear the filters to see everything." />
        </div>
      ) : (
        <ul className="space-y-2">
          {data!.data.map((m) => (
            <li key={m.id}>
              <button
                type="button"
                onClick={() => setSelected(m.id)}
                className="panel flex w-full flex-wrap items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-surface-overlay"
              >
                <div className="min-w-0">
                  <p className="text-base font-medium">{m.legal_name}</p>
                  <p className="numeral mt-1 text-xs text-content-faint">
                    {m.merchant_ref} · {m.entity_type.replace(/_/g, ' ').toLowerCase()} ·{' '}
                    {m.industry_category.replace(/_/g, ' ').toLowerCase()}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge status={m.kyc_status} />
                  <StatusBadge status={m.risk_tier} />
                  {m.is_frozen === 1 && <span className="badge badge-danger">frozen</span>}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
